import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, ChevronDown, X, Gift, ShoppingBag } from 'lucide-react';
interface TopbarProps {
  onAIClick?: () => void;
}
interface Notification {
  id: string;
  type: 'riscatto' | 'utilizzo';
  title: string;
  message: string;
  time: string;
  count: number;
  link: string;
  unread: boolean;
}
const Topbar: React.FC<TopbarProps> = ({
  onAIClick
}) => {
  const navigate = useNavigate();
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([{
    id: '1',
    type: 'riscatto',
    title: '3 nuovi Riscatti',
    message: 'Nuovi premi riscattati dai tuoi utenti',
    time: '5 min fa',
    count: 3,
    link: '/claims?filter=riscatti',
    unread: true
  }, {
    id: '2',
    type: 'utilizzo',
    title: '5 nuovi Utilizzi',
    message: 'Premi utilizzati nelle tue sedi',
    time: '12 min fa',
    count: 5,
    link: '/claims?filter=utilizzi',
    unread: true
  }, {
    id: '3',
    type: 'riscatto',
    title: '2 nuovi Riscatti',
    message: 'Kit Cosmetici Solidi molto richiesti',
    time: '1 ora fa',
    count: 2,
    link: '/claims?filter=riscatti',
    unread: false
  }]);
  const unreadCount = notifications.filter(n => n.unread).length;
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    setNotifications(prev => prev.map(n => n.id === notification.id ? {
      ...n,
      unread: false
    } : n));
    // Navigate to link
    navigate(notification.link);
    setShowNotifications(false);
  };
  const handleMarkAllRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      unread: false
    })));
  };
  const handleLogout = () => {
    // Close the account menu
    setShowAccountMenu(false);
    // Navigate to login page
    navigate('/login');
  };
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'riscatto':
        return <Gift className="h-5 w-5 text-gray-500" data-id="element-705" />;
      case 'utilizzo':
        return <ShoppingBag className="h-5 w-5 text-gray-500" data-id="element-706" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" data-id="element-707" />;
    }
  };
  return <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm" data-id="element-708">
      <div className="px-4 py-3 flex items-center gap-4" data-id="element-709">
        {/* Right side - Actions */}
        <div className="flex items-center gap-3 ml-auto" data-id="element-710">
          {/* Notifications */}
          <div className="relative" data-id="element-711">
            <button className="rounded-full p-2 text-gray-500 hover:bg-gray-100 relative" title="Notifiche" onClick={() => setShowNotifications(!showNotifications)} data-id="element-712">
              <Bell className="h-5 w-5" data-id="element-713" />
              {unreadCount > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" data-id="element-714"></span>}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && <>
                {/* Backdrop for mobile */}
                <div className="fixed inset-0 z-30 md:hidden" onClick={() => setShowNotifications(false)} data-id="element-715"></div>

                <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-gray-200 z-40" data-id="element-716">
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between" data-id="element-717">
                    <h3 className="font-semibold text-gray-900" data-id="element-718">Notifiche</h3>
                    {notifications.length > 0 && <button onClick={handleMarkAllRead} className="text-xs text-greenback hover:text-greenback/80 font-medium" data-id="element-719">
                        Segna tutte lette
                      </button>}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-[400px] overflow-y-auto" data-id="element-720">
                    {notifications.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-721">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" data-id="element-722" />
                        <p className="text-sm" data-id="element-723">Nessuna notifica</p>
                      </div> : <div className="divide-y divide-gray-100" data-id="element-724">
                        {notifications.map(notification => <div key={notification.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${notification.unread ? 'bg-blue-50/50' : ''}`} onClick={() => handleNotificationClick(notification)} data-id="element-725">
                            <div className="flex items-start gap-3" data-id="element-726">
                              <div className="flex-shrink-0 mt-0.5" data-id="element-727">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-728">
                                <div className="flex items-start justify-between gap-2" data-id="element-729">
                                  <p className="text-sm font-medium text-gray-900" data-id="element-730">
                                    {notification.title}
                                  </p>
                                  {notification.unread && <span className="flex-shrink-0 h-2 w-2 rounded-full bg-greenback mt-1.5" data-id="element-731"></span>}
                                </div>
                                <p className="text-sm text-gray-600 mt-0.5" data-id="element-732">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 mt-1" data-id="element-733">
                                  {notification.time}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>
                </div>
              </>}
          </div>

          {/* Account Menu */}
          <div className="relative" data-id="element-734">
            <button className="flex items-center gap-2 pl-2 border-l border-gray-200 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors" onClick={() => setShowAccountMenu(!showAccountMenu)} data-id="element-735">
              <div className="h-8 w-8 rounded-full bg-greenback flex items-center justify-center overflow-hidden" data-id="element-736">
                <img src="https://blog.lasaponaria.it/hubfs/LaSaponaria-5.jpg" alt="La Saponaria" className="h-full w-full object-cover" data-id="element-737" />
              </div>
              <span className="text-sm font-medium hidden md:block" data-id="element-738">
                La Saponaria
              </span>
              <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-739" />
            </button>

            {showAccountMenu && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50" data-id="element-740">
                <a href="/profile" className="block px-4 py-2 text-sm hover:bg-gray-50 rounded-t-lg" data-id="element-741">
                  <div className="flex items-center" data-id="element-742">
                    <User className="h-4 w-4 mr-2" data-id="element-743" />
                    Profilo Partner
                  </div>
                </a>
                <div className="border-t border-gray-100" data-id="element-744">
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 rounded-b-lg" data-id="element-745">
                    Esci
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </header>;
};
export default Topbar;